import { defineComponent as _defineComponent } from 'vue'
import { renderList as _renderList, Fragment as _Fragment, openBlock as _openBlock, createElementBlock as _createElementBlock, createElementVNode as _createElementVNode, createVNode as _createVNode, createCommentVNode as _createCommentVNode } from "vue"

const _hoisted_1 = { class: "row mb-3" }
const _hoisted_2 = ["src", "alt"]
const _hoisted_3 = { class: "row mb-3" }
const _hoisted_4 = { class: "col" }
const _hoisted_5 = { class: "input-group" }
const _hoisted_6 = { class: "custom-file" }
const _hoisted_7 = ["id", "disabled"]
const _hoisted_8 = {
  key: 0,
  class: "input-group-append"
}
const _hoisted_9 = ["disabled"]

import { onBeforeMount, ref } from 'vue'
import { guid, promiseParallel } from '@/helpers/tools'
import { v4 as uuid } from 'uuid'
import { uploadedFilesApi } from '@/di/api'
import AppIconLoadingButton from "@/components/app/AppIconLoadingButton.vue";

interface ImagePropsItem {
  id: string,
  filename: string
}

interface UploadedImage extends ImagePropsItem {
  isPersisted: boolean,
  file: File | null,
  url: string
}

interface Props {
  images?: ImagePropsItem[],
  confirmOnRemove?: boolean,
  autoUpload?: boolean,
}


export default /*#__PURE__*/_defineComponent({
  __name: 'AppUploadImages',
  props: {
    images: { default: () => [] },
    confirmOnRemove: { type: Boolean, default: false },
    autoUpload: { type: Boolean, default: true }
  },
  emits: ["fileUploadDone"],
  setup(__props: any, { emit }: { emit: ({
  (e: 'fileUploadDone', uploadedFiles: ImagePropsItem[]): void
}), expose: any, slots: any, attrs: any }) {

const props = __props as { images: ImagePropsItem[], confirmOnRemove: boolean, autoUpload: boolean };





const componentId = ref<string>(guid())
const filesInput = ref<HTMLInputElement | null>(null)
const uploadedImages = ref<UploadedImage[]>([])
const lock = ref<{ removingId: string | undefined, uploadingIds: string[] }>({
  removingId: undefined,
  uploadingIds: []
})

async function upload () {
  try {
    await promiseParallel(
        uploadedImages.value.filter(({ isPersisted }) => !isPersisted)
            .map(({ id, file }) => ({
              promise: () => uploadedFilesApi.uploadFile(id, file),
              resolve: (response: { data: { id: string, filename: string, url: string } }) => {
                const uploadedImage = uploadedImages.value.filter((ui) => ui.id === response.data.id)[0]

                if (uploadedImage) {
                  uploadedImage.filename = response.data.filename
                  uploadedImage.url = response.data.url
                  uploadedImage.isPersisted = true
                  uploadedImage.file = null

                  lock.value.uploadingIds = lock.value.uploadingIds.filter(uploadingId => uploadingId !== response.data.id)
                }
              }
            })),
        {
          beforeAll: (): void => {
            lock.value.uploadingIds = uploadedImages.value
                .filter(({ isPersisted }): boolean => !isPersisted)
                .map(({ id }): string => id)
          },
          afterAll: triggerEventOnImageListChanged,
          pending: 1,
        }
    )
  } finally {
    lock.value.uploadingIds = []
  }
}

function triggerEventOnImageListChanged () {
  emit('fileUploadDone', uploadedImages.value.map(({ id, filename }) => ({ id, filename })))
}

async function onFilesChanged () {
  uploadedImages.value = [
    ...uploadedImages.value.filter(({ isPersisted }) => isPersisted),
    ...[...Array(filesInput?.value?.files?.length).keys()].map((index) => {
      const file = filesInput?.value?.files?.item(index) as File

      return {
        id: uuid(),
        filename: file.name,
        url: URL.createObjectURL(file),
        isPersisted: false,
        file
      }
    })
  ]

  // @ts-ignore
  filesInput.value.value = null

  filesInput.value?.blur()

  if (props.autoUpload) {
    await upload()
  }
}

async function remove (uploadedImageIdToRemove: string) {
  if (props.confirmOnRemove && !confirm('Are you sure?')) {
    return
  }

  lock.value.removingId = uploadedImageIdToRemove

  try {
    if (
        props.images.filter(({ id }) => id === uploadedImageIdToRemove).length === 0
        && uploadedImages.value.filter(({ id }) => id === uploadedImageIdToRemove)[0]?.isPersisted
    ) {
      await uploadedFilesApi.removeUploadedFile(uploadedImageIdToRemove)
    }

    uploadedImages.value = uploadedImages.value.filter(({ id }) => id !== uploadedImageIdToRemove)
  } finally {
    lock.value.removingId = undefined
  }
}

onBeforeMount(() => {
  uploadedImages.value = props.images.map((image) => ({
    id: image.id,
    filename: image.filename,
    // @ts-ignore
    url: `${API_URL}/uploaded/images/${image.filename}`, // eslint-disable-line
    isPersisted: true,
    file: null
  }))
})

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock(_Fragment, null, [
    _createElementVNode("div", _hoisted_1, [
      (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(uploadedImages.value, (uploadedImage) => {
        return (_openBlock(), _createElementBlock("div", {
          key: uploadedImage.id,
          class: "col col-6",
          style: {"position":"relative"}
        }, [
          _createElementVNode("img", {
            src: uploadedImage.url,
            alt: uploadedImage.filename,
            class: "img-thumbnail",
            style: {"width":"100%"}
          }, null, 8, _hoisted_2),
          _createVNode(AppIconLoadingButton, {
            icon: "trash",
            loading: lock.value.uploadingIds.includes(uploadedImage.id) || lock.value.removingId === uploadedImage.id,
            disabled: !!lock.value.removingId || lock.value.uploadingIds.length > 0,
            "button-type": "danger",
            "button-small": "",
            style: {"position":"absolute","top":"8px","right":"8px"},
            onClick: ($event: any) => (remove(uploadedImage.id))
          }, null, 8, ["loading", "disabled", "onClick"])
        ]))
      }), 128))
    ]),
    _createElementVNode("div", _hoisted_3, [
      _createElementVNode("div", _hoisted_4, [
        _createElementVNode("div", _hoisted_5, [
          _createElementVNode("div", _hoisted_6, [
            _createElementVNode("input", {
              id: `upload-file-input-${componentId.value}`,
              ref_key: "filesInput",
              ref: filesInput,
              type: "file",
              class: "form-control",
              accept: "image/*",
              multiple: "",
              disabled: lock.value.uploadingIds.length > 0,
              onChange: onFilesChanged
            }, null, 40, _hoisted_7)
          ]),
          (!props.autoUpload)
            ? (_openBlock(), _createElementBlock("div", _hoisted_8, [
                _createElementVNode("button", {
                  type: "button",
                  class: "btn btn-info",
                  style: {"margin":"0"},
                  disabled: lock.value.uploadingIds.length > 0,
                  onClick: upload
                }, " Upload ", 8, _hoisted_9)
              ]))
            : _createCommentVNode("", true)
        ])
      ])
    ])
  ], 64))
}
}

})